import React, { useState, useEffect, useRef } from "react";

const parentClassname = "react-form";

const Field = props => {
    // Classes
    const componentClassname = parentClassname
        ? `${parentClassname}__field`
        : "field";
    const labelPlaceholderClassname = props.labelplaceholder
        ? `${componentClassname}__label--labelplaceholder`
        : "";

    // Refs
    const inputRef = useRef();
    const optionsListRef = useRef();
    const selectContainerRef = useRef();

    // State variables
    const [options, setOptions] = useState([]);
    const [showOptions, _setShowOptions] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    // Conveneience methods for showing the options dropdown
    const openOptions = () => {
        _setShowOptions(true);
    };

    // Conveneience methods for hiding the options dropdown
    const hideOptions = () => {
        _setShowOptions(false);
    };

    // Effect for closing the options dropdown after a window click
    useEffect(() => {
        if (showOptions) {
            window.addEventListener("click", hideOptions);
        } else {
            window.removeEventListener("click", hideOptions);
        }
        return () => {
            window.removeEventListener("click", hideOptions);
        };
    }, [showOptions]);

    // Effect for setting up the selectedOptions based on what is passed
    // to the input
    useEffect(() => {
        if (inputRef.current) {
            const rawOptions = Array.from(inputRef.current.options);
            const newOptions = rawOptions.map((option, index) => ({
                value: option.value,
                display: option.textContent,
                index,
            }));
            if (newOptions.length > 0) {
                setOptions(newOptions);
                setSelectedOption(newOptions[0]);
            }
        }
    }, [inputRef]);

    // Handles the keydown event for the dropdown container
    // Expected behaviour:
    // - Pressing Enter/ArrowUp/ArrowDown should open the dropdown and focus the selected option
    const handleContainerKeydown = event => {
        switch (event.key) {
            case "Enter":
            case "ArrowUp":
            case "ArrowDown":
                event.preventDefault();
                openOptions();
                // This is on a set timeout because you can't focus an element
                // that is display: none - we're essentially waiting until the next React render
                setTimeout(() => {
                    if (optionsListRef.current) {
                        optionsListRef.current
                            .querySelector(
                                `[data-value="${selectedOption.value}"]`
                            )
                            .focus();
                    }
                }, 1);
                break;
            default:
                break;
        }
    };

    // Handles the keydown event for each individual option
    // Expected bahaviour:
    // - TAB: Nothing should happen
    // - ArrowDown - Focus next option in listbox (Stop when reached end)
    // - ArrowUp - Focus previous option in listbox (Stop when reached start)
    // - Escape - Close the listbox and focus the container element
    // - Enter - Selects the current focused option
    const handleOptionKeydown = event => {
        switch (event.key) {
            case "Tab":
                event.preventDefault();
                break;
            case "ArrowDown":
                event.preventDefault();
                if (event.target) {
                    const next = event.target.nextElementSibling;
                    if (next) {
                        next.focus();
                    }
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (event.target) {
                    const prev = event.target.previousElementSibling;
                    if (prev) {
                        prev.focus();
                    }
                }
                break;
            case "Escape":
            case "Esc":
                event.preventDefault();
                hideOptions();
                if (selectContainerRef.current) {
                    selectContainerRef.current.focus();
                }
                break;
            case "Enter":
                selectOption(event);
                break;
            default:
                break;
        }
    };

    // Convenience method for selecting an option
    const selectOption = event => {
        event.preventDefault();
        hideOptions();
        if (selectContainerRef.current) {
            selectContainerRef.current.focus();
        }
        if (event.target) {
            const value = event.target.dataset.value;
            const index = event.target.dataset.index;
            const display = event.target.textContent;
            setSelectedOption({ value, display, index });
            if (props.onCustomSelectChange) {
                props.onCustomSelectChange(value);
            }
        }
    };

    return (
        <div
            className={`${componentClassname} 
				${componentClassname}--${props.type}
				${props.classes.inheritedClasses} 
				${props.classes.isDisabled} 
				${props.classes.isIncomplete} 
				${props.classes.isFocused} 
				${props.classes.hasValue}
				${props.classes.isValid}
				${props.classes.isInvalid}
				custom-select
			`}
        >
            <label htmlFor={props.accessibilityIds.input}>
                {/*label*/}
                {props.label ? (
                    <div
                        className={`${componentClassname}__label  ${labelPlaceholderClassname}`}
                    >
                        <div
                            className={`${componentClassname}__label__inner`}
                            id={props.accessibilityIds.label}
                        >
                            {props.label}
                            {props.required ? null : (
                                <span
                                    className={`${componentClassname}__label__optional-label`}
                                >
                                    {" "}
                                    (optional)
                                </span>
                            )}
                        </div>
                    </div>
                ) : null}

                {/*input element*/}
                {React.cloneElement(props.inputElement, {
                    ref: inputRef,
                })}

                {/* Fake element control */}
                {selectedOption ? (
                    <div
                        className={`select-container ${
                            showOptions ? "show-options" : ""
                        }`}
                    >
                        <div
                            className="select-container__input"
                            role="button"
                            tabIndex="0"
                            onKeyDown={handleContainerKeydown}
                            onClick={openOptions}
                            ref={selectContainerRef}
                            aria-labelledby={`${props.accessibilityIds.label} ${props.accessibilityIds.label}-option-${selectedOption.index}`}
                            aria-haspopup="listbox"
                            aria-expanded={showOptions}
                        >
                            <div className="select-container__input__text">
                                {selectedOption.display}
                            </div>
                            <div className="select-container__input__icon">
                                <i className="icon icon-chevron-down"></i>
                            </div>
                        </div>
                        <div
                            className="select-container__options"
                            aria-expanded={showOptions}
                        >
                            <div
                                className="select-container__options__inner"
                                ref={optionsListRef}
                                role="listbox"
                            >
                                {options.map((option, index) => (
                                    <li
                                        key={option.value}
                                        tabIndex="0"
                                        className="select-container__option"
                                        onKeyDown={handleOptionKeydown}
                                        data-value={option.value}
                                        data-index={index}
                                        onClick={selectOption}
                                        role="option"
                                        aria-selected={
                                            selectedOption.value ===
                                            option.value
                                        }
                                        id={`${props.accessibilityIds.label}-option-${index}`}
                                    >
                                        {option.display}
                                    </li>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null}
            </label>

            {/*errors*/}
            {!props.hideErrors &&
            props.errors &&
            props.errors.length &&
            props.showErrors ? (
                <div className={`${componentClassname}__errors`}>
                    <div
                        className={`${componentClassname}__errors__item`}
                        id={props.accessibilityIds.error}
                    >
                        Error: {props.errors[0]}
                    </div>
                </div>
            ) : null}

            {/*info*/}
            {props.info ? (
                <div
                    className={`${componentClassname}__info`}
                    id={props.accessibilityIds.description}
                >
                    {props.info}
                </div>
            ) : null}
        </div>
    );
};

export default Field;
